import { PrimaryButton, DefaultButton, TextField, Stack, Modal, Text, Spinner } from "@fluentui/react"
import { APIError, ChatMessage, ChatResponse, Conversation, ConversationRequest, ConversationWithError, getTaskDownloadUrl, conversationGenerateStart, transcribe, conversationStream } from "../../api"
import { useContext, useEffect, useState } from "react"
import { AppStateContext } from "../../state/AppProvider"
import { useNavigate } from "react-router-dom";
import uuid from "react-uuid";

interface MinutesTranscriptionDialogProps {
    /** モーダルの開閉状態 */
    isOpen: boolean;
    /** モーダルの開閉状態を変更する関数 */
    setIsOpen: (isOpen: boolean) => void;
}
export const MinutesTranscriptionDialog = ({
    isOpen,
    setIsOpen,
}: MinutesTranscriptionDialogProps) => {
    const defualtSystemPrompt = "あなたは会議の書き起こしから議事録を生成するアシスタントです。先頭にその会議のタイトル（例：○○会議 議事録）を書き、その後に会議の議事録をまとめてください。"

    const [isWaiting, setIsWaiting] = useState(false);
    const [successTranscribeTaskId, setSuccessTranscribeTaskId] = useState<null | string>(null)
    const [result, setResult] = useState<null | Conversation | APIError>(null);
    const [systemPrompt, setSystemPrompt] = useState(defualtSystemPrompt)
    const appStateContext = useContext(AppStateContext);
    const navigate = useNavigate()

    useEffect(() => {
        setSystemPrompt(defualtSystemPrompt)
        setSuccessTranscribeTaskId(null)
    }, [isOpen])

    /** 音声データ送信処理 */
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const file = formData.get("audioFile") as File;
        const max_speakers = formData.get("maxSpeakers");
        const meeting_datetime = String(formData.get("meetingDateTime") ?? "");
        setIsWaiting(true);
        const messages: ChatMessage[] = []
        let conversation: Conversation | null = null

        try {    
            const dt = meeting_datetime.trim().length == 0 ? undefined : new Date(meeting_datetime);
            const max_spk = max_speakers === null ? undefined : Number(max_speakers)
            
            // 音声文字起こしAPIの呼び出し
            const result = await transcribe(
                file,
                max_spk,
                dt
            );

            if ("error" in result) {
                setResult(result);
                return;
            }

            setSuccessTranscribeTaskId(result.task_id)
            // 書き起こし結果を使って会話を生成
            messages.push(
                {
                    id: uuid(),
                    role: "user",
                    content: result.result,
                    date: new Date().toISOString(),
                }
            )
            const request: ConversationRequest = {
                messages: messages
            };
            const model_index = String(appStateContext?.state.selectedGPTModelIndex)
            let system_prompt = systemPrompt
            if (dt){
                const formatted_datetime = `${dt.getFullYear()}年${String(dt.getMonth() + 1).padStart(2, '0')}月${String(dt.getDate()).padStart(2, '0')}日 ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
                system_prompt += `\n会議日時は「${formatted_datetime}」です。`
            }
            else{
                system_prompt += "\n会議日時は議事録に記載しないでください。"
            }
            
            let error: string | undefined = undefined
            let generateResult: ChatResponse | undefined = undefined
            const onProgress = (res: ChatResponse) => {}
            const onComplete = (res: ChatResponse) => {
                generateResult = res
            }
            const onError = (statusCode: number, message?: string) => {
                error = message ?? "議事録の生成に失敗しました。"
            }

            const generating_conv = await conversationGenerateStart(request, [], model_index,
                onError, undefined, undefined, undefined, undefined, undefined, system_prompt)
            if (generating_conv === undefined) {
                setResult({"error": error ?? "予期せぬエラーが発生しました"})
                return
            }
            await conversationStream(generating_conv.task_id, onError, onProgress, onComplete)

            if (error !== undefined || generateResult === undefined) {
                setResult({"error": error ?? "予期せぬエラーが発生しました"})
                return
            }

            // 生成されたメッセージを追加
            const genResult = generateResult as unknown as ChatResponse
            if (genResult.choices?.[0]?.messages) {
                genResult.choices[0].messages.forEach((msg) => {
                    messages.push({
                        ...msg,
                        date: new Date().toISOString()
                    });
                });
            }

            // 議事録の生成に成功
            conversation = {
                id: genResult.history_metadata.conversation_id,
                title: genResult.history_metadata.title,
                messages: messages,
                date: new Date().toISOString(),
                user_id: appStateContext?.state.loginUserInfo?.id ?? ""
            };
            setResult(conversation)
        } finally {
            if (conversation) {
                const currentChatHistory = appStateContext?.state.chatHistory ?? [];
                currentChatHistory.unshift(conversation);
                if (appStateContext?.state.isRAGEnabled) {
                    appStateContext?.dispatch({ type: "SET_IS_RAG_ENABLED", payload: {isEnabled: false, openRAGMenuIfToggleToOpen: false} });
                }
                appStateContext?.dispatch({
                    type: "FETCH_CHAT_HISTORY",
                    payload: [...currentChatHistory],
                });
                navigate(`/chat/${conversation.id}`)
            }
            setIsWaiting(false);
        }
    };

    return (
        <div style={{position: "absolute"}}>
        <Modal isOpen={isOpen}>
            {/* 初期表示 */}
            {!isWaiting && !result && (
                <Stack
                    style={{
                        marginLeft: "20px",
                        marginRight: "20px",
                        width: "30vw",
                        minWidth: "300px",
                    }}
                >
                    <Stack
                        horizontal
                        tokens={{ childrenGap: "10px" }}
                        verticalAlign="center"
                        id="messagemodalcontent-header"
                    >
                        <h2>議事録生成</h2>
                        <Text>※完了まで数分かかることがあります</Text>
                    </Stack>
                    <form onSubmit={handleSubmit}>
                        <Stack
                            style={{
                                marginTop: "15px",
                                marginBottom: "20px",
                            }}
                            tokens={{ childrenGap: "10px" }}
                        >
                            {/* 音声ファイルアップロード */}
                            <input type="file" required name="audioFile" />

                            {/* 発話人数 */}
                            <span>
                                <span style={{ fontWeight: "bold", marginRight: "10px" }}>
                                    発話人数
                                </span>
                                <span style={{ color: "rgb(96, 94, 92)", fontSize: "12px" }}>
                                    ※任意
                                </span>
                            </span>
                            <div style={{ width: "56px", marginTop: "4px" }}>
                                <TextField
                                    name="maxSpeakers"
                                    type="number"
                                    min="2"
                                />
                            </div>
                            <p style={{ color: "rgb(96, 94, 92)", fontSize: "12px" }}>指定した場合、話者を識別します</p>

                            {/* 会議の日時 */}
                            <span>
                                <span style={{ fontWeight: "bold", marginRight: "10px" }}>
                                    会議の日時
                                </span>
                                <span style={{ color: "rgb(96, 94, 92)", fontSize: "12px" }}>
                                    ※任意
                                </span>
                            </span>
                            <div style={{ width: "160px", marginTop: "4px" }}>
                                <TextField
                                    name="meetingDateTime"
                                    type="datetime-local"
                                />
                            </div>

                            {/* システムプロンプト */}
                            <TextField 
                                multiline 
                                rows={5} 
                                label="指示プロンプト" 
                                value={systemPrompt}
                                onChange={(_, v) => { setSystemPrompt(v ?? "")}}
                            />
                        </Stack>
                        <Stack
                            horizontal
                            style={{ margin: "20px" }}
                            tokens={{ childrenGap: "20px" }}
                            horizontalAlign="end"
                        >
                            <PrimaryButton type="submit" text="実行" />
                            <DefaultButton
                                onClick={setIsOpen.bind(null, false)}
                                text="キャンセル"
                            />
                        </Stack>
                    </form>
                </Stack>
            )}

            {/* 生成中 */}
            {isWaiting && (
                <Stack
                    style={{
                        display: isWaiting ? "block" : "none",
                        marginLeft: "20px",
                        marginRight: "20px",
                        width: "340px",
                    }}
                >
                    <Stack
                        horizontal
                        tokens={{ childrenGap: "10px" }}
                        verticalAlign="center"
                        id="messagemodalcontent-header"
                    >
                        <Spinner size={3} />
                        <h2>議事録生成中……</h2>
                    </Stack>
                    <Stack
                        horizontal
                        style={{ width: "100%" }}
                        tokens={{ childrenGap: "20px" }}
                    >
                        <Stack
                            style={{
                                marginTop: "15px",
                                marginBottom: "20px",
                            }}
                        >
                            <Text>議事録を生成しています</Text>
                            <Text>この処理には数分かかることがあります</Text>
                            <Text>ページを離れないでください</Text>
                        </Stack>
                    </Stack>
                </Stack>
            )}

            {/* 結果表示 */}
            {!isWaiting && result && (
                <Stack
                    style={{
                        display: !isWaiting && result ? "block" : "none",
                        marginLeft: "20px",
                        marginRight: "20px",
                        width: "340px",
                    }}
                >
                    <Stack
                        horizontal
                        tokens={{ childrenGap: "10px" }}
                        verticalAlign="center"
                        id="messagemodalcontent-header"
                    >
                        <h2>
                            {result && "error" in result ? "エラー" : "完了"}
                        </h2>
                    </Stack>
                    <Stack
                        style={{ marginTop: "15px", marginBottom: "20px" }}
                        tokens={{ childrenGap: "10px" }}
                    >
                        <Text style={{whiteSpace: "pre-wrap"}}>
                            {"error" in result
                                ? `エラーが発生しました。\n${result.error}`
                                : "議事録の生成が完了しました"}
                        </Text>
                        {
                            successTranscribeTaskId && (
                            <DefaultButton 
                                iconProps={{iconName: "Download"}}
                                href={getTaskDownloadUrl(successTranscribeTaskId)}
                            >
                                書き起こし結果のみをダウンロード
                            </DefaultButton>)
                        }
                    </Stack>
                    <Stack
                        horizontal
                        style={{ margin: "20px" }}
                        tokens={{ childrenGap: "20px" }}
                        horizontalAlign="end"
                    >
                        <PrimaryButton
                            text="OK"
                            onClick={() => {
                                setResult(null);
                                setIsOpen(false);
                            }}
                        />
                    </Stack>
                </Stack>
            )}
        </Modal>
        </div>
    );
};
